import React, { Fragment } from 'react';
import { ChartSeriesV2, ChartContextData, SeriesValue } from 'src/components/ui/chart/types';
import { makeStyles, Tooltip } from '@material-ui/core';
import { isEqual } from 'date-fns';

const BAR_COLOR_BACKGROUND = 'rgba(0,0,0, 0.12)';
const BAR_OPACITY = 0.9;
const BAR_HEIGHT = 16;

const STRIPE_WIDTH = 5;
const STRIPE_LINE_POSITION = 2;
const STRIPE_LINE_WIDTH = 2;
const MIN_WIDTH = 2;

const useStyles = makeStyles(() => ({
  bar: {
    transition: 'opacity 0.1s ease',
    '&:hover': {
      opacity: '1 !important',
      '& rect': {
        opacity: '1 !important',
      },
    },
  },
}));

export enum BarColor {
  GREEN = '#28a745',
  RED = '#dc3545',
}

interface BarsProps {
  group: ChartSeriesV2;
  beforeStartRangePoint?: SeriesValue;
  context: ChartContextData;
  ranges: { xMin: Date; xMax: Date }[];
  meaningId: number;
  color: BarColor;
  fullRanges: { xMin: Date; xMax: Date }[];
}

interface Accumulator {
  bars: JSX.Element[];
  currentStart: number;
}

const calculateRangePositions = (
  startDate: Date,
  endDate: Date,
  rangeMin: Date,
  rangeMax: Date,
  contextWidth: number
) => {
  const rangeWidth = rangeMax.getTime() - rangeMin.getTime();
  const startOffset = startDate.getTime() - rangeMin.getTime();
  const endOffset = endDate.getTime() - rangeMin.getTime();

  const originalRelativeStart = startOffset / rangeWidth;
  const relativeStart = Math.max(0, Math.min(originalRelativeStart, 1));
  const relativeWidth = Math.max(0, (endOffset - startOffset) / rangeWidth);
  const calculatedWidth = contextWidth * relativeWidth;

  // calculate width based on different cases
  // we want to ensure the bar is not bigger than the context width
  // and if it is outside the render area, we simply don't render it
  let width = calculatedWidth;

  // Case 1: If it's the last point and reaches the end of the range
  if (endDate.getTime() === rangeMax.getTime()) {
    width = contextWidth - contextWidth * relativeStart;
  }
  // Case 2: If the bar starts before the visible area (x < 0)
  else if (originalRelativeStart < 0) {
    width = calculatedWidth - Math.abs(originalRelativeStart) * contextWidth;
  }
  // Case 3: If the bar starts within the visible area but extends beyond the end
  else if (originalRelativeStart > 0 && originalRelativeStart + relativeWidth > 1) {
    width = contextWidth - contextWidth * originalRelativeStart;
  }

  // Ensure the width is within the allowed limits
  const finalWidth = Math.max(MIN_WIDTH, Math.min(width, contextWidth));

  return {
    x: Math.max(0, Math.min(contextWidth * relativeStart, contextWidth)),
    width: finalWidth,
  };
};

const generateTooltipContent = (
  startDate: Date | null,
  endDate: Date | null,
  hideStartDate: boolean,
  hideEndDate: boolean
): NonNullable<React.ReactNode> => {
  if (hideStartDate) {
    return endDate ? `To: ${endDate.toLocaleDateString()} ${endDate.toLocaleTimeString()}` : '';
  }

  if (hideEndDate) {
    return startDate
      ? `From: ${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString()}`
      : '';
  }

  return (
    <>
      {startDate && `From: ${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString()}`}
      {startDate && endDate && <br />}
      {endDate && `To: ${endDate.toLocaleDateString()} ${endDate.toLocaleTimeString()}`}
    </>
  );
};

const addBar = (
  startIndex: number,
  endIndex: number,
  group: ChartSeriesV2 & { values: (SeriesValue & { hideStartDate?: boolean })[] },
  context: ChartContextData,
  ranges: { xMin: Date; xMax: Date }[],
  className: string,
  color: BarColor,
  hideEndDate: boolean
) => {
  const startDate = group.values[startIndex].x;
  const endDate =
    endIndex < group.values.length - 1 ? group.values[endIndex + 1].x : ranges[0].xMax;

  if (startDate > ranges[0].xMax || endDate < ranges[0].xMin) {
    return null;
  }

  const { x, width } = calculateRangePositions(
    startDate,
    endDate,
    ranges[0].xMin,
    ranges[0].xMax,
    context.size.width
  );

  if (width === 0) {
    return null;
  }

  return group.values[startIndex].hideStartDate && hideEndDate ? (
    <rect
      key={group.key}
      x={x}
      y={0}
      width={width}
      height={BAR_HEIGHT}
      fill={color}
      opacity={BAR_OPACITY}
      className={className}
    />
  ) : (
    <Tooltip
      key={`${group.key}-${startIndex}`}
      title={generateTooltipContent(
        startDate,
        endDate,
        group.values[startIndex].hideStartDate ?? false,
        hideEndDate
      )}
      placement="top"
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={2000}
    >
      <rect
        x={x}
        y={0}
        width={width}
        height={BAR_HEIGHT}
        fill={color}
        opacity={BAR_OPACITY}
        className={className}
      />
    </Tooltip>
  );
};

const Bars: React.FC<BarsProps> = ({
  group,
  beforeStartRangePoint,
  context,
  ranges,
  meaningId,
  color,
  fullRanges,
}) => {
  const classes = useStyles();
  const backgroundPatternId = `stripes-background-${color.replace('#', '')}-${group.key}`;
  const hasExtraPoint =
    beforeStartRangePoint !== undefined && !isEqual(beforeStartRangePoint?.x, group.values[0]?.x);

  const groupData = hasExtraPoint
    ? {
        ...group,
        values: [
          {
            hideStartDate: true,
            x: ranges[0].xMin,
            y: beforeStartRangePoint.y,
          },
          ...group.values,
        ],
      }
    : group;

  // check if there is a point before the end of the range
  const lastPointBeforeEndDate =
    groupData.values.filter((point) => point.x <= ranges[0].xMax).pop()?.y ?? 0;
  // check if the last point before the end of the range has data
  const hasLastPointData = lastPointBeforeEndDate > 0;
  const bars = groupData.values.reduce<Accumulator>(
    (acc, point, index, values) => {
      if (point.y === meaningId) {
        if (acc.currentStart === -1) {
          acc.currentStart = index;
        }
        // if range has data and if the current point is the last point we need to hide the end date
        // because we don't know if there is data after the end of the range
        const hideEndDate = hasLastPointData && index === values.length - 1;

        if (index === values.length - 1 || values[index + 1].y !== meaningId) {
          const bar = addBar(
            acc.currentStart,
            index,
            groupData,
            context,
            ranges,
            classes.bar,
            color,
            hideEndDate
          );
          if (bar) acc.bars.push(bar);
          acc.currentStart = -1;
        }
      }
      return acc;
    },
    { bars: [], currentStart: -1 }
  ).bars;

  return (
    <Fragment key={group.key}>
      <defs>
        <pattern
          id={backgroundPatternId}
          patternUnits="userSpaceOnUse"
          width={STRIPE_WIDTH}
          height={BAR_HEIGHT}
        >
          <line
            x1={STRIPE_LINE_POSITION}
            y1="0"
            x2={STRIPE_LINE_POSITION}
            y2={BAR_HEIGHT}
            stroke={BAR_COLOR_BACKGROUND}
            strokeWidth={STRIPE_LINE_WIDTH}
          />
        </pattern>
      </defs>
      <rect
        x={0}
        y={0}
        width={context.size.width}
        height={BAR_HEIGHT}
        fill={`url(#${backgroundPatternId})`}
        opacity={BAR_OPACITY}
        className={classes.bar}
      />
      {bars}
    </Fragment>
  );
};

export default React.memo(Bars);
